import React from 'react';
import { AlertTriangle } from 'lucide-react';

export function BagCloudChart({ clubs }) {
  if (!clubs || clubs.length < 2) return null;

  const width = 400;
  const height = 300;
  const padding = { top: 30, right: 30, bottom: 40, left: 50 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  // Estimate dispersion based on carry distance (longer shots = more dispersion)
  // Typical dispersion: ~3-5% of carry distance for good players
  const estimateDispersion = (carry) => {
    const lateralSpread = carry * 0.04; // 4% lateral spread
    const carrySpread = carry * 0.03;   // 3% carry spread
    return { lateral: lateralSpread, carry: carrySpread };
  };

  // Build club data with dispersion ellipses
  const clubData = clubs.map((club, idx) => {
    const carry = club.carry || 150;
    const disp = estimateDispersion(carry);
    // Color based on category
    const colors = {
      Driver: '#3b82f6',
      Wood: '#8b5cf6',
      Hybrid: '#06b6d4',
      Iron: '#10b981',
      Wedge: '#f59e0b',
      Putter: '#6b7280'
    };
    return {
      ...club,
      centerX: 0,
      centerY: carry,
      radiusX: disp.lateral,
      radiusY: disp.carry,
      color: colors[club.category] || '#6b7280'
    };
  });

  // Find overlapping clubs (Section 3B)
  const findOverlaps = () => {
    const overlaps = [];
    for (let i = 0; i < clubData.length; i++) {
      for (let j = i + 1; j < clubData.length; j++) {
        const a = clubData[i];
        const b = clubData[j];
        // Check if ellipses overlap significantly (centers within combined radii)
        const carryDiff = Math.abs(a.centerY - b.centerY);
        const combinedCarryRadius = a.radiusY + b.radiusY;
        const overlapRatio = 1 - (carryDiff / combinedCarryRadius);
        if (overlapRatio > 0.5) {
          overlaps.push({
            club1: a.name || 'Club',
            club2: b.name || 'Club',
            overlap: Math.round(overlapRatio * 100)
          });
        }
      }
    }
    return overlaps;
  };

  const overlaps = findOverlaps();

  // Scale functions
  const carries = clubData.map(c => c.centerY);
  const maxCarry = Math.max(...carries) + 20;
  const minCarry = Math.min(...carries) - 20;
  const maxLateral = Math.max(...clubData.map(c => c.radiusX)) * 2;

  const scaleX = (lat) => padding.left + ((lat + maxLateral) / (maxLateral * 2)) * chartW;
  const scaleY = (carry) => padding.top + chartH - ((carry - minCarry) / (maxCarry - minCarry)) * chartH;
  const scaleRX = (r) => (r / maxLateral) * chartW / 2;
  const scaleRY = (r) => (r / (maxCarry - minCarry)) * chartH;

  return (
    <div className="space-y-4">
      <svg width={width} height={height} className="bg-zinc-950 rounded-lg mx-auto">
        {/* Grid lines */}
        <line x1={scaleX(0)} y1={padding.top} x2={scaleX(0)} y2={height - padding.bottom} stroke="#3f3f46" strokeDasharray="3,3" />

        {/* Y-axis labels */}
        <text x={padding.left - 10} y={padding.top + 5} fill="#71717a" fontSize="10" textAnchor="end">{maxCarry}</text>
        <text x={padding.left - 10} y={height - padding.bottom} fill="#71717a" fontSize="10" textAnchor="end">{minCarry}</text>
        <text x={15} y={height / 2} fill="#71717a" fontSize="10" textAnchor="middle" transform={`rotate(-90, 15, ${height / 2})`}>Carry (yds)</text>

        {/* X-axis label */}
        <text x={width / 2} y={height - 10} fill="#71717a" fontSize="10" textAnchor="middle">Lateral Dispersion</text>

        {/* Dispersion ellipses */}
        {clubData.map((club, i) => (
          <g key={i}>
            <ellipse
              cx={scaleX(club.centerX)}
              cy={scaleY(club.centerY)}
              rx={scaleRX(club.radiusX)}
              ry={scaleRY(club.radiusY)}
              fill={club.color}
              fillOpacity={0.25}
              stroke={club.color}
              strokeWidth={2}
            />
            {/* Club label */}
            <text
              x={scaleX(club.centerX)}
              y={scaleY(club.centerY)}
              fill="white"
              fontSize="9"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {(club.name || 'Club').length > 12 ? (club.name || 'Club').substring(0, 12) + '...' : (club.name || 'Club')}
            </text>
          </g>
        ))}

        {/* Legend */}
        <g transform={`translate(${width - 80}, ${padding.top})`}>
          {['Driver', 'Wood', 'Hybrid', 'Iron', 'Wedge'].map((cat, i) => {
            const colors = { Driver: '#3b82f6', Wood: '#8b5cf6', Hybrid: '#06b6d4', Iron: '#10b981', Wedge: '#f59e0b' };
            if (!clubData.some(c => c.category === cat)) return null;
            return (
              <g key={cat} transform={`translate(0, ${i * 14})`}>
                <circle cx={5} cy={5} r={4} fill={colors[cat]} />
                <text x={12} y={8} fill="#a1a1aa" fontSize="8">{cat}</text>
              </g>
            );
          })}
        </g>
      </svg>

      {/* Overlap Warnings (Section 3B) */}
      {overlaps.length > 0 && (
        <div className="bg-amber-950/30 border border-amber-800/50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-amber-400 font-medium text-sm mb-2">
            <AlertTriangle size={14} /> Potential "Dead Clubs" Detected
          </div>
          <ul className="space-y-1 text-xs text-zinc-400">
            {overlaps.map((o, i) => (
              <li key={i}>
                <span className="text-amber-300">{o.club1}</span> and <span className="text-amber-300">{o.club2}</span> overlap by ~{o.overlap}%
              </li>
            ))}
          </ul>
          <p className="text-xs text-zinc-500 mt-2">Consider adjusting lofts or removing redundant clubs.</p>
        </div>
      )}
    </div>
  );
}
